/**
 * Admin Layout - Shared sidebar layout for all admin section pages
 * Provides consistent navigation across Academy, Resources, Build, Protect, Grow, Finance, Analytics, Google Analytics, Messages, Documents
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  GraduationCap, Layers, Shield, TrendingUp, DollarSign, BarChart3,
  LogOut, Menu, X, Home, Crown, ChevronRight, Package, LineChart,
  MessageCircle, FolderOpen, Users
} from 'lucide-react';

const ADMIN_SECTIONS = [
  { 
    id: 'clients', 
    label: 'Client Profiles', 
    icon: Users, 
    path: '/admin/clients',
    color: '#D4AF37',
    description: 'Unified Client Management'
  },
  { 
    id: 'messages', 
    label: 'Messages', 
    icon: MessageCircle, 
    path: '/admin/messages',
    color: '#06B6D4',
    description: 'Chat & Load Offers'
  },
  { 
    id: 'documents', 
    label: 'Documents', 
    icon: FolderOpen, 
    path: '/admin/documents',
    color: '#6366F1',
    description: 'Document Vault'
  },
  { 
    id: 'academy', 
    label: 'Academy', 
    icon: GraduationCap, 
    path: '/admin/academy',
    color: '#A855F7',
    description: 'Courses & Training'
  },
  { 
    id: 'resources', 
    label: 'Resources', 
    icon: Package, 
    path: '/admin/resources',
    color: '#EC4899',
    description: 'Templates & Downloads'
  },
  { 
    id: 'build', 
    label: 'Build', 
    icon: Layers, 
    path: '/admin/build',
    color: '#8B5CF6',
    description: 'New Carriers & Onboarding'
  },
  { 
    id: 'protect', 
    label: 'Protect', 
    icon: Shield, 
    path: '/admin/protect',
    color: '#10B981',
    description: 'Active Operations'
  },
  { 
    id: 'grow', 
    label: 'Grow', 
    icon: TrendingUp, 
    path: '/admin/grow',
    color: '#F59E0B',
    description: 'Scaling & Expansion'
  },
  { 
    id: 'finance', 
    label: 'Finance', 
    icon: DollarSign, 
    path: '/admin/finance',
    color: '#22C55E',
    description: 'Revenue & Payments'
  },
  { 
    id: 'analytics', 
    label: 'Analytics', 
    icon: BarChart3, 
    path: '/admin/analytics',
    color: '#3B82F6',
    description: 'KPIs & Reports'
  },
  { 
    id: 'google-analytics', 
    label: 'Google Analytics', 
    icon: LineChart, 
    path: '/admin/google-analytics',
    color: '#EA4335',
    description: 'Traffic & Conversions'
  }
];

const AdminLayout = ({ children, title, subtitle }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  const currentSection = ADMIN_SECTIONS.find(s => location.pathname.startsWith(s.path));
  
  const handleLogout = async () => {
    try {
      // Call backend logout to clear cookies
      await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    // Clear localStorage
    localStorage.removeItem('adminToken');
    localStorage.removeItem('adminUser');
    // Force navigation to login
    navigate('/admin/login', { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#050505] flex">
      {/* Noise Overlay */}
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-40
        w-72 bg-[#0A0A0A]/95 backdrop-blur-xl border-r border-white/5
        transform transition-transform duration-300 ease-in-out
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        flex flex-col
      `}>
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <Link to="/admin" className="flex items-center gap-3 group">
            <div className="w-11 h-11 bg-[#D4AF37] flex items-center justify-center gold-glow-hover transition-all duration-300">
              <Crown className="w-6 h-6 text-black" />
            </div>
            <div>
              <h1 className="text-white font-black text-lg tracking-tight group-hover:text-[#D4AF37] transition-colors">QUEEN B</h1>
              <p className="text-[#D4AF37] text-[10px] uppercase tracking-[0.2em] font-semibold">Command Center</p>
            </div>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {/* Home Link */}
          <Link
            to="/admin"
            className={`
              flex items-center gap-3 px-4 py-3 transition-all duration-200
              ${location.pathname === '/admin' 
                ? 'bg-white/10 text-white border-l-2 border-[#D4AF37]' 
                : 'text-white/50 hover:bg-white/5 hover:text-white border-l-2 border-transparent'}
            `}
          >
            <Home className="w-5 h-5" />
            <span className="font-semibold tracking-wide">Command Hub</span>
          </Link>

          <div className="pt-6 pb-3">
            <p className="px-4 text-[10px] text-white/30 uppercase tracking-[0.2em] font-semibold">Sections</p>
          </div>

          {ADMIN_SECTIONS.map((section) => {
            const Icon = section.icon;
            const isActive = location.pathname.startsWith(section.path);
            
            return (
              <Link
                key={section.id}
                to={section.path}
                onClick={() => setSidebarOpen(false)}
                className={`
                  flex items-center gap-3 px-4 py-3 transition-all duration-200 group
                  ${isActive 
                    ? 'bg-[#D4AF37] text-black' 
                    : 'text-white/50 hover:bg-white/5 hover:text-white border-l-2 border-transparent hover:border-white/20'}
                `}
              >
                <Icon className="w-5 h-5" style={{ color: isActive ? 'black' : section.color }} />
                <div className="flex-1">
                  <span className="font-semibold tracking-wide">{section.label}</span>
                  {!isActive && (
                    <p className="text-[10px] text-white/30 group-hover:text-white/40 uppercase tracking-wider">{section.description}</p>
                  )}
                </div>
                {isActive && <ChevronRight className="w-4 h-4" />}
              </Link>
            );
          })}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/5">
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-all duration-200"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </aside>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen">
        {/* Top Bar */}
        <header className="glass border-b border-white/5 px-6 py-4 flex items-center justify-between sticky top-0 z-20">
          <div className="flex items-center gap-4">
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="lg:hidden text-white/50 hover:text-white transition-colors"
            >
              {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>

            {/* Breadcrumb / Title */}
            <div>
              <div className="flex items-center gap-2 text-sm">
                <Link to="/admin" className="text-white/40 hover:text-white transition-colors">Admin</Link>
                {currentSection && (
                  <>
                    <span className="text-white/20">/</span>
                    <span className="text-[#D4AF37] font-semibold">{currentSection.label}</span>
                  </>
                )}
              </div>
              {title && <h1 className="text-white font-bold text-xl mt-1">{title}</h1>}
              {subtitle && <p className="text-white/50 text-sm">{subtitle}</p>}
            </div>
          </div>

          {/* Current Section Badge */}
          {currentSection && (
            <div 
              className="hidden md:flex items-center gap-2 px-4 py-2 rounded"
              style={{ backgroundColor: `${currentSection.color}20`, border: `1px solid ${currentSection.color}40` }}
            >
              <currentSection.icon className="w-4 h-4" style={{ color: currentSection.color }} />
              <span style={{ color: currentSection.color }} className="text-sm font-medium">
                {currentSection.label} Section
              </span>
            </div>
          )}
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 sm:p-6 overflow-x-hidden">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
export { ADMIN_SECTIONS };
